import React from 'react';
import { useApp } from '../../context/AppContext';

// Cada passo tem uma função check(state) — o progresso é detectado automaticamente
const STEPS = [
  {
    id: 'account',
    icon: 'fa-building-columns',
    color: '#185FA5',
    title: 'Crie sua primeira conta',
    desc: 'Cadastre a conta bancária que você usa no dia a dia.',
    tab: 'accounts',
    action: 'Ir para Contas',
    check: s => s.accounts.length > 0,
  },
  {
    id: 'transaction',
    icon: 'fa-plus',
    color: '#1d9e75',
    title: 'Registre seu primeiro lançamento',
    desc: 'Adicione uma receita ou despesa para começar a acompanhar seu dinheiro.',
    tab: 'transactions',
    action: 'Novo Lançamento',
    check: s => s.transactions.length > 0,
  },
  {
    id: 'card',
    icon: 'fa-credit-card',
    color: '#8A05BE',
    title: 'Cadastre seu cartão de crédito',
    desc: 'Controle o limite e acompanhe a fatura do seu cartão.',
    tab: 'accounts',
    action: 'Ir para Cartões',
    check: s => s.cards.length > 0,
  },
  {
    id: 'savings-goal',
    icon: 'fa-piggy-bank',
    color: 'var(--gold)',
    title: 'Defina sua meta de economia mensal',
    desc: 'Quanto você quer guardar por mês? O Dashboard acompanha seu progresso.',
    tab: 'dashboard',
    action: 'Ir para o Dashboard',
    check: s => (s.savingsGoalMonthly || 0) > 0,
  },
  {
    id: 'ten-tx',
    icon: 'fa-list-ul',
    color: '#378ADD',
    title: 'Registre 10 lançamentos',
    desc: 'Com mais dados, os relatórios e a Saúde Financeira ficam precisos.',
    tab: 'transactions',
    action: 'Ir para Lançamentos',
    check: s => s.transactions.length >= 10,
  },
  {
    id: 'goal',
    icon: 'fa-star',
    color: 'var(--gold-light)',
    title: 'Crie uma meta financeira',
    desc: 'Viagem, reserva de emergência, carro novo — dê um objetivo ao seu dinheiro.',
    tab: 'goals',
    action: 'Criar Meta',
    check: s => s.goals.length > 0,
    pro: true,
  },
  {
    id: 'budget',
    icon: 'fa-clipboard-list',
    color: '#0F6E56',
    title: 'Monte seu orçamento',
    desc: 'Defina limites de gasto por categoria e receba alertas.',
    tab: 'budgets',
    action: 'Criar Orçamento',
    check: s => s.budgets.length > 0,
    pro: true,
  },
  {
    id: 'vehicle',
    icon: 'fa-car',
    color: '#993556',
    title: 'Cadastre seu veículo',
    desc: 'Registre manutenções e receba lembretes de troca de óleo e revisões.',
    tab: 'vehicles',
    action: 'Ir para Veículos',
    check: s => s.vehicles.length > 0,
  },
  {
    id: 'ai',
    icon: 'fa-robot',
    color: '#5c0381',
    title: 'Experimente o Consultor IA',
    desc: 'Análises personalizadas do seu dinheiro com inteligência artificial.',
    tab: 'ai-consultor',
    action: 'Abrir Consultor IA',
    check: s => localStorage.getItem('maw_ai_used') === '1',
    pro: true,
  },
];

export default function GettingStarted() {
  const { state, dispatch } = useApp();

  const done = STEPS.filter(s => s.check(state));
  const pct = Math.round((done.length / STEPS.length) * 100);
  const allDone = done.length === STEPS.length;

  const go = (tab) => dispatch({ type: 'SET_TAB', payload: tab });

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Primeiros Passos</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Complete os passos e domine o MAW Finanças</div>
      </div>

      {/* Progresso geral */}
      <div className="card" style={{ marginBottom: 16, background: allDone ? 'linear-gradient(135deg, #0F6E56, #1D9E75)' : 'linear-gradient(135deg, var(--navy), #1e3a52)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 40 }}>{allDone ? '🏆' : '🚀'}</div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>
              {allDone ? 'Parabéns! Você completou tudo!' : `${done.length} de ${STEPS.length} passos concluídos`}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
              {allDone ? 'Você já domina o MAW Finanças como um profissional.' : 'Continue — cada passo deixa seu controle financeiro mais completo.'}
            </div>
            <div style={{ height: 8, borderRadius: 99, background: 'rgba(255,255,255,0.2)', overflow: 'hidden', marginTop: 10 }}>
              <div style={{ height: '100%', width: `${pct}%`, background: 'var(--gold)', borderRadius: 99, transition: 'width 0.5s ease' }} />
            </div>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--gold)' }}>{pct}%</div>
        </div>
      </div>

      {/* Lista de passos */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {STEPS.map((step, i) => {
          const isDone = step.check(state);
          return (
            <div key={step.id} className="card" style={{ marginBottom: 0, opacity: isDone ? 0.75 : 1, display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px' }}>
              {/* Número / check */}
              <div style={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                background: isDone ? 'rgba(29,158,117,0.12)' : `${step.color}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isDone
                  ? <i className="fas fa-check" style={{ color: 'var(--green)', fontSize: 16 }}></i>
                  : <i className={`fas ${step.icon}`} style={{ color: step.color, fontSize: 15 }}></i>}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', textDecoration: isDone ? 'line-through' : 'none', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--muted)', fontWeight: 600, fontSize: 12 }}>{i + 1}.</span>
                  {step.title}
                  {step.pro && <span style={{ fontSize: 9, background: 'var(--gold)', color: '#0d1b2a', borderRadius: 99, padding: '2px 7px', fontWeight: 700 }}>PRO</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{step.desc}</div>
              </div>

              {!isDone && (
                <button onClick={() => go(step.tab)} className="btn btn-ghost" style={{ fontSize: 12, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {step.action} <i className="fas fa-arrow-right" style={{ marginLeft: 4, fontSize: 10 }}></i>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Dica final */}
      <div style={{ marginTop: 16, background: 'rgba(var(--gold-rgb),0.08)', border: '1px solid rgba(var(--gold-rgb),0.3)', borderRadius: 12, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <i className="fas fa-lightbulb" style={{ color: 'var(--gold)', marginTop: 2 }}></i>
        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
          <strong>Dica:</strong> precisa de ajuda em algum passo? A <strong>Central de Ajuda</strong> tem guias detalhados de cada módulo, e o <strong>Consultor IA</strong> pode analisar seus dados e sugerir os próximos movimentos.
        </div>
      </div>
    </div>
  );
}
